import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SidebarProvider } from './context/SidebarContext';
import { AuthProvider } from './context/AuthContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { ThemeProvider } from './context/ThemeContext';
import { ModalProvider } from './context/ModalContext';


import ImpersonationBanner from './components/ImpersonationBanner';
import MainLayout from './components/MainLayout';
import Dashboard from './components/Dashboard';
import WhatsappPhones from './pages/WhatsappPhones';
import CloudSessions from './pages/CloudSessions';
import BrowserSessions from './pages/BrowserSessions';
import SendViaExcel from './pages/SendViaExcel';
import InboxPage from './components/inbox/InboxPage';
import VerifyNumbers from './pages/VerifyNumbers';
import GroupExport from './pages/GroupExport';
import ImageToURL from './pages/ImageToURL';
import WebhookIntegration from './pages/WebhookIntegration';
import ShopifyIntegration from './pages/ShopifyIntegration';
import FlowBuilder from './pages/FlowBuilder';
import Settings from './pages/settings/Settings';
import Pricing from './pages/Pricing';
import PricingINR from './pages/PricingINR';
import Reports from './pages/Reports';
import Unibox from './pages/Unibox';

// ✅ Docs — Conceptual guides + Scalar API reference (per-endpoint pages
// retired in favour of the OpenAPI-driven Swagger view served from the API).
import IntegrationDocs from './pages/docs/IntegrationDocs';
import GettingStarted from './pages/docs/GettingStarted';
import Overview from './pages/docs/Overview';
import Authentication from './pages/docs/Authentication';
import TryIt from './pages/docs/TryIt';
import SwaggerView from './pages/docs/SwaggerView';

// ✅ Auth
import Signup from './pages/Signup';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import VerifyOtp from './pages/VerifyOtp';
import ProtectedRoute from './components/ProtectedRoute';

// ✅ Admin
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import Plans from './pages/admin/ManagePlans';
import Coupons from './pages/admin/ManageCoupons';
import Users from './pages/admin/ManageUsers';
import Features from './pages/admin/ManageFeatures';

import AdminStorage from './pages/admin/AdminStorage';
import AdminWebhooks from './pages/admin/AdminWebhooks';
import ManageEmailTemplates from './pages/admin/ManageEmailTemplates';
import AdminDataManagement from './pages/admin/AdminDataManagement';
import ManageAutomations from './pages/admin/ManageAutomations';
import ManageSubscriptions from './pages/admin/ManageSubscriptions';
import AssignPlan from './pages/admin/AssignPlan';
import AdminSession from './pages/admin/AdminSession';
import AdminSessionLogs from './pages/admin/AdminSessionLogs';
import AdminChangelog from './pages/admin/AdminChangelog';
import AdminProxyPool from './pages/admin/AdminProxyPool';


// ✅ Guide (public)
import GuideIndex from './pages/guide/GuideIndex';
import GettingStartedGuide from './pages/guide/GettingStartedGuide';
import FlowBuilderGuide from './pages/guide/FlowBuilderGuide';
import AIFeaturesGuide from './pages/guide/AIFeaturesGuide';
import CampaignsGuide from './pages/guide/CampaignsGuide';
import CRMGuide from './pages/guide/CRMGuide';
import SendViaExcelGuide from './pages/guide/SendViaExcelGuide';
import NumberVerificationGuide from './pages/guide/NumberVerificationGuide';
import GroupExportGuide from './pages/guide/GroupExportGuide';
import ImageToURLGuide from './pages/guide/ImageToURLGuide';
import WhatsAppWidgetGuide from './pages/guide/WhatsAppWidgetGuide';

// ✅ Stripe Payment Result Pages
import Success from './pages/Success';
import Cancel from './pages/Cancel';

// ✅ Team Invite
import AcceptInvite from './pages/AcceptInvite';
import WhatsAppWidget from './pages/WhatsAppWidget';
import CRM from './pages/CRM';
import Campaigns from './pages/Campaigns';
import CampaignAnalytics from './pages/CampaignAnalytics';
import FeatureGate from './components/FeatureGate';
import QuotaExceededModal from './components/QuotaExceededModal';
import QuotaBanner from './components/QuotaBanner';


function RootRedirect() {
  // Check role in localStorage as a hint (actual auth is verified via httpOnly cookie on API calls)
  const role = localStorage.getItem('role');
  return <Navigate to={role ? '/dashboard' : '/signup'} replace />;
}

function App() {
  return (
    <ThemeProvider>
    <ModalProvider>
    <AuthProvider>
      <SidebarProvider>
      <WorkspaceProvider>
        <SubscriptionProvider>
        <ImpersonationBanner />
        <QuotaBanner />
        <QuotaExceededModal />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Stripe Checkout result routes */}
          <Route path="/success" element={<Success />} />
          <Route path="/cancel" element={<Cancel />} />

          {/* Team invite */}
          <Route path="/invite/:token" element={<AcceptInvite />} />

          {/* User Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
          </Route>

          <Route path="/whatsapp-phones" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<FeatureGate featureKey="qrSessions"><WhatsappPhones /></FeatureGate>} />
          </Route>

          <Route path="/cloud-sessions" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<FeatureGate featureKey="cloudApi"><CloudSessions /></FeatureGate>} />
          </Route>

          <Route path="/browser/sessions" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<BrowserSessions />} />
          </Route>

          <Route path="/unibox" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<Unibox />} />
          </Route>

          <Route path="/inbox" element={<Navigate to="/unibox" replace />} />

          <Route path="/send-via-excel" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<FeatureGate featureKey="sendViaExcel"><SendViaExcel /></FeatureGate>} />
          </Route>

          <Route path="/number-verification" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<FeatureGate featureKey="verification"><VerifyNumbers /></FeatureGate>} />
          </Route>

          <Route path="/group-members-export" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<GroupExport />} />
          </Route>

          <Route path="/image-to-url" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<FeatureGate featureKey="imageToUrl"><ImageToURL /></FeatureGate>} />
          </Route>

          <Route path="/whatsapp-widget" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<FeatureGate featureKey="whatsappWidget"><WhatsAppWidget /></FeatureGate>} />
          </Route>

          <Route path="/webhook-integration" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<WebhookIntegration />} />
          </Route>

          <Route path="/shopify" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<FeatureGate featureKey="flowBuilder"><ShopifyIntegration /></FeatureGate>} />
          </Route>

          <Route path="/flow-builder" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<FeatureGate featureKey="flowBuilder"><FlowBuilder /></FeatureGate>} />
          </Route>

          <Route path="/campaigns" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<FeatureGate featureKey="outreach"><Campaigns /></FeatureGate>} />
            <Route path="new" element={<FeatureGate featureKey="outreach"><Campaigns /></FeatureGate>} />
            <Route path=":campaignId" element={<FeatureGate featureKey="outreach"><Campaigns /></FeatureGate>} />
            <Route path=":campaignId/analytics" element={<FeatureGate featureKey="outreach"><CampaignAnalytics /></FeatureGate>} />
            <Route path=":campaignId/:tab" element={<FeatureGate featureKey="outreach"><Campaigns /></FeatureGate>} />
          </Route>

          <Route path="/nurturing" element={<Navigate to="/campaigns" replace />} />

          {/* Guide routes (inside app layout) */}
          <Route path="/guide" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<GuideIndex />} />
            <Route path="getting-started" element={<GettingStartedGuide />} />
            <Route path="flow-builder" element={<FlowBuilderGuide />} />
            <Route path="ai-features" element={<AIFeaturesGuide />} />
            <Route path="campaigns" element={<CampaignsGuide />} />
            <Route path="crm" element={<CRMGuide />} />
            <Route path="send-via-excel" element={<SendViaExcelGuide />} />
            <Route path="number-verification" element={<NumberVerificationGuide />} />
            <Route path="group-export" element={<GroupExportGuide />} />
            <Route path="image-to-url" element={<ImageToURLGuide />} />
            <Route path="whatsapp-widget" element={<WhatsAppWidgetGuide />} />
          </Route>

          <Route path="/settings" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<Settings />} />
          </Route>

          <Route path="/pricing" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<Pricing />} />
            <Route path="inr" element={<PricingINR />} />
          </Route>

          <Route path="/reports" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<Reports />} />
          </Route>

          <Route path="/crm" element={<Navigate to="/unibox" replace />} />

          {/* ✅ Admin Protected Routes */}
          <Route
            path="/admin"
            element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>}
          >
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="plans" element={<Plans />} />
            <Route path="coupons" element={<Coupons />} />
            <Route path="users" element={<Users />} />
            <Route path="features" element={<Features />} />
            <Route path="storage" element={<AdminStorage />} />
            <Route path="proxy-pool" element={<AdminProxyPool />} />
            <Route path="developer" element={<SwaggerView scope="admin" showIntegrations={false} />} />
                  <Route path="webhook" element={<AdminWebhooks />} />
            <Route path="email-templates" element={<ManageEmailTemplates />} />
            <Route path="automations" element={<ManageAutomations />} />
            <Route path="subscriptions" element={<ManageSubscriptions />} />
            <Route path="assign-plan" element={<AssignPlan />} />
            <Route path="session" element={<AdminSession />} />
            <Route path="session-logs" element={<AdminSessionLogs />} />
            <Route path="data" element={<AdminDataManagement />} />
            <Route path="changelog" element={<AdminChangelog />} />
          </Route>

          {/* Docs (login-gated) — conceptual guides + Scalar-powered API reference.
              Per-endpoint hand-written pages are retired; old links redirect to Swagger UI
              so existing bookmarks don't break. */}
          <Route path="/integration-and-api" element={<ProtectedRoute><IntegrationDocs /></ProtectedRoute>}>
            <Route index element={<Overview />} />
            <Route path="overview" element={<Overview />} />
            <Route path="authentication" element={<Authentication />} />
            <Route path="try-it" element={<TryIt />} />
            <Route path="swagger" element={<SwaggerView />} />
            <Route path="getting-started" element={<GettingStarted />} />
            {/* Backward-compatible redirects for retired per-endpoint pages */}
            <Route path="overview/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="auth/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="sessions/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="messaging/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="groups/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="verification/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="history/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="webhooks/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="inbox/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="contacts/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="flows/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="campaigns/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="outreach/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="media/*" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="api-reference" element={<Navigate to="/integration-and-api/swagger" replace />} />
            <Route path="*" element={<Navigate to="/integration-and-api/swagger" replace />} />
          </Route>
        </Routes>
        </SubscriptionProvider>
      </WorkspaceProvider>
      </SidebarProvider>
    </AuthProvider>
    </ModalProvider>
    </ThemeProvider>
  );
}

export default App;
